import React from 'react';
import { ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import uuid from 'react-uuid';
import { GPC } from 'api/models/GPC';
import { LTM, NTM } from 'common/constants/financials';
import {
  DATA_UNAVAILABLE,
  DEFAULT_ZERO_VALUE,
  SELECTION_A,
  SELECTION_B,
  VALUATIONS_DEFAULT_CURRENCY,
} from 'common/constants/valuations';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  ADJUSTED_EBITDA_FOR_YEARS,
  BENCHMARK_ALIASES,
  FORWARD_NET_INCOME,
  MAP_ADJUSTED_EBITDA_SUFFIX_VALUE,
  MEAN_ALIAS,
  MEDIAN_ALIAS,
  NET_INCOME_EXCLUDE_EXTRA_ITEMS,
  PERCENTILE_25,
  PERCENTILE_75,
  PERCENTILE_SELECTION_A_ALIAS,
  PERCENTILE_SELECTION_B_ALIAS,
  PRICE_TO_EARNINGS_ABV,
  SELECTED_MULTIPLE,
  SELECTED_MULTIPLE_VALUE,
  SEVENTY_FIFTH_ALIAS,
  SPECIFIED_LABEL,
  TANGIBLE_BOOK_VALUE,
  TANGIBLE_BOOK_VALUE_ESTIMATE,
  TWENTY_FIFTH_ALIAS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { percentileOptions } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/getRowConfig';
import { PUBLIC_COMPANIES, SORT_ASC } from 'pages/Valuations/util/constants';
import { CapIQ, PublicComp } from 'pages/ValuationsAllocation/types';
import ValuationService from 'services/valuations';
import { Logger } from 'utilities';
import {
  CalendarYearsFinancials,
  GetFinancialPeriodValueProps,
  GetTitleWithIconProps,
  GetTransposedFinancialsProps,
  ParseGpcComparisonProps,
  SynchronizeAndUpdateApproachesProps,
  SynchronizeCalendarYearsProps,
  TitleIconWrapperProps,
} from './types';

export const parseGpcComparison = ({ data, attributes }: ParseGpcComparisonProps) => {
  const gpc = data;
  const { comp_group_id, comp_group_name, ...newGpc } = gpc;

  let calendarYearsFinancials: Record<string, Record<string, string>> = {};

  const as_of_dates: string[] = [];
  const flattened = Object.keys(newGpc).reduce((flattenedObj: any, key: any) => {
    const gpcCapIQ = gpc[key as keyof GPC] as CapIQ;
    // if key starts with year, 2016_revenue
    if (Date.parse(key.slice(0, 4))) {
      const [year, prop] = key.split('_');
      if (calendarYearsFinancials[year]) {
        calendarYearsFinancials[year] = {
          ...calendarYearsFinancials[year],
          [prop]: gpcCapIQ?.value,
        };
      } else {
        calendarYearsFinancials = {
          ...calendarYearsFinancials,
          [year]: {
            [prop]: gpcCapIQ?.value,
          },
        };
      }
    } else {
      const { value, properties } = gpcCapIQ;
      const { asofdate } = properties ?? {};

      const attributeItem = attributes[key as keyof typeof attributes];
      // eslint-disable-next-line no-param-reassign
      flattenedObj[key]
        = typeof value === 'number' && attributeItem
          ? parseFloat(Number(value).toFixed(Number(attributeItem?.decimal_places)))
          : value;
      if (asofdate) {
        as_of_dates.push(asofdate);
      }
    }
    return flattenedObj;
  }, {}) as Record<string, any>;
  flattened.as_of_date = as_of_dates.length > 0 ? as_of_dates[0] : gpc.as_of_date;
  flattened.first_metric_enabled = true;
  flattened.second_metric_enabled = true;
  flattened.first_forward_metric_enabled = true;
  flattened.second_forward_metric_enabled = true;
  flattened.name = flattened.company_name;
  flattened.calendar_years_financials = calendarYearsFinancials;
  flattened.reference_for_backsolve = flattened.cap_iq_id;

  if (comp_group_id && comp_group_name) {
    flattened.comp_group_id = comp_group_id;
    flattened.comp_group_name = comp_group_name;
    flattened.temp_ref = comp_group_id;
  }
  return flattened;
};

const comparisonPropToRead = (periodType: number, type: string) => {
  if (periodType === LTM) {
    return type === PRICE_TO_EARNINGS_ABV ? NET_INCOME_EXCLUDE_EXTRA_ITEMS : TANGIBLE_BOOK_VALUE;
  }
  return type === PRICE_TO_EARNINGS_ABV ? FORWARD_NET_INCOME : TANGIBLE_BOOK_VALUE_ESTIMATE;
};

/**
 * @param {Object} comparison Comparison Object.
 * @param {Array} periods Financials Statement Periods.
 * @param {number} periodId Selected financial statement period id.
 * @param {string} type ebitda, revenue, price (price-to-earnings) or tangible (tangible book value).
 */
export const getFinancialPeriodValue = ({
  comparison,
  periods,
  periodId,
  type,
  isEvaluatingEquityValue,
}: GetFinancialPeriodValueProps) => {
  if (!periodId) {
    return '';
  }
  const period = periods.find(p => p?.id?.toString() === periodId?.toString());

  if (period) {
    // If the period is LTM or NTM, we need to look up the ltm_adjusted_ebitda or ntm_adjusted_ebitda.
    if (Number(period.period_type) === LTM) {
      return !isEvaluatingEquityValue
        ? comparison[`ltm_${type === ADJUSTED_EBITDA_FOR_YEARS ? MAP_ADJUSTED_EBITDA_SUFFIX_VALUE : type}` as keyof GPC]
        : comparison[comparisonPropToRead(LTM, type)];
    }

    if (Number(period.period_type) === NTM) {
      return !isEvaluatingEquityValue
        ? comparison[`ntm_${type === ADJUSTED_EBITDA_FOR_YEARS ? MAP_ADJUSTED_EBITDA_SUFFIX_VALUE : type}` as keyof GPC]
        : comparison[comparisonPropToRead(NTM, type)];
    }

    const year = period?.statement_date?.slice(0, 4);
    return comparison.calendar_years_financials?.[year!]?.[type];
  }

  return '';
};

export const getTransposedFinancials = ({ periods, financials, approach }: GetTransposedFinancialsProps) => {
  let financialsInfo = { ...financials };

  // eslint-disable-next-line max-len
  const { ltm_financial_statement_period_id: ltmPeriodId, ntm_financial_statement_period_id: ntmPeriodId } = approach;

  if (ltmPeriodId) {
    const ltmPeriod = periods.find(p => p?.id?.toString() === ltmPeriodId?.toString());

    if (ltmPeriod) {
      financialsInfo = {
        ...financialsInfo,
        ltm_ebitda: ltmPeriod?.income_statement?.ebitda,
        ltm_revenue: ltmPeriod?.income_statement?.total_revenue,
      };
    }
  }

  if (ntmPeriodId) {
    const ntmPeriod = periods.find(p => p?.id?.toString() === ntmPeriodId?.toString());

    if (ntmPeriod) {
      financialsInfo = {
        ...financialsInfo,
        ntm_ebitda: ntmPeriod?.income_statement?.ebitda,
        ntm_revenue: ntmPeriod?.income_statement?.total_revenue,
      };
    }
  }

  return financialsInfo;
};

/**
 * Parse tickerSymbol
 * @param {string} tickerSymbol - Ticker Symbol of the GPC
 * @returns {string} Ticker Symbol parsed
 */
export const getTicker = (tickerSymbol: string) =>
  tickerSymbol ? tickerSymbol.toLowerCase().replaceAll('.', '') : DATA_UNAVAILABLE.toLowerCase().replaceAll(' ', '');

/**
 *
 * @param {Object} comparison
 * @param {number} comparison.id
 * @param {number} comparison.valuation_approach_gpc
 * @param {string} comparison.comp_group_name
 * @param {Date} comparison.created_at
 * @param {Date} comparison.updated_at
 * @param {string} comparison.name
 * @param {string} comparison.ticker_symbol
 * @param {string} comparison.company_name
 * @param {string} comparison.cap_iq_id
 * @param {string} comparison.exchange
 * @param {string} comparison.currency
 * @param {Date} comparison.as_of_date
 * @param {string} comparison.close_price - Stock Price
 * @param {string} comparison.market_cap - Market Cap
 * @param {string} comparison.enterprise_value - Enterprise Value
 * @param {string} comparison.ltm_revenue
 * @param {string} comparison.ltm_revenue_growth
 * @param {string} comparison.ltm_ebitda
 * @param {string} comparison.ltm_ebitda_margin
 * @param {string} comparison.ltm_gross_margin
 * @param {string} comparison.ntm_revenue
 * @param {string} comparison.ntm_revenue_growth
 * @param {string} comparison.fy_plus1_revenue
 * @param {string} comparison.fy_plus2_revenue
 * @param {string} comparison.ntm_ebitda
 * @param {string} comparison.ntm_gross_margin
 * @param {string} comparison.ntm_ebitda_margin
 * @param {string} comparison.fy_plus1_ebitda
 * @param {string} comparison.fy_plus2_ebitda
 * @param {string} comparison.current_portion_of_debt
 * @param {string} comparison.short_term_debt
 * @param {string} comparison.current_debt
 * @param {string} comparison.current_portion_of_leases
 * @param {string} comparison.long_term_debt
 * @param {string} comparison.non_current_debt
 * @param {string} comparison.capital_lease_liabilities
 * @param {string} comparison.effective_tax_rate
 * @param {string} comparison.cost_borrowing
 * @param {string} comparison.beta_5yr
 * @param {string} comparison.volume
 * @param {string} comparison.price_volume_historical_1yr
 * @param {string} comparison.price_volume_historical_2yr
 * @param {string} comparison.price_volume_historical_5yr
 * @param {boolean} comparison.first_metric_enabled
 * @param {boolean} comparison.second_metric_enabled
 * @param {boolean} comparison.first_forward_metric_enabled
 * @param {boolean} comparison.second_forward_metric_enabled
 * @param {string} comparison.calendar_years_financials
 * @param {string} comparison.gpc_approach_comp_group
 * @returns {comparison} Comparison checked and parsed
 */

export const checkGpcComparison = (comparison: GPC) => {
  const requiredFieldsNumbers = ['close_price', 'market_cap', 'enterprise_value'];
  const requiredFieldsNumbersNonZeros = [
    'first_metric',
    'second_metric',
    'first_forward_metric',
    'second_forward_metric',
  ];
  const requiredFieldsBooleans = [
    'first_metric_enabled',
    'second_metric_enabled',
    'first_forward_metric_enabled',
    'second_forward_metric_enabled',
  ];

  const comparisonChecked = Object.fromEntries(
    Object.entries(comparison).map(([key, value]) => {
      if (key === 'cap_iq_id') {
        return [key, value ?? uuid()];
      }

      if (key === 'company_name') {
        return [key, value ?? comparison.name ?? ''];
      }

      if (key === 'ticker_symbol') {
        return [key, value ?? DATA_UNAVAILABLE];
      }

      if (requiredFieldsNumbers.includes(key)) {
        return [key, value ?? DEFAULT_ZERO_VALUE];
      }

      if (requiredFieldsNumbersNonZeros.includes(key)) {
        return [key, value ?? DEFAULT_ZERO_VALUE];
      }

      if (requiredFieldsBooleans.includes(key)) {
        return [key, typeof value === 'boolean' ? value : false];
      }

      return [key, value];
    })
  );

  requiredFieldsNumbersNonZeros.forEach(comparisonKey => {
    if (comparisonChecked[comparisonKey] === DEFAULT_ZERO_VALUE) {
      comparisonChecked[`${comparisonKey}_enabled`] = false;
    }
  });

  return comparisonChecked;
};

/**
 * Synchronize Calendar Years for GPC
 * @param {array} valuationApproaches
 * @param {array} financialsPeriods
 * @param {Object} measurementDate
 * @param {Object} companyInfo
 * @param {Function} setSyncComparisons
 * @param {Function} setApproaches
 * @returns {void}
 */
export const synchronizeCalendarYears = async ({
  valuationApproaches,
  financialsPeriods,
  measurementDate,
  companyInfo,
  setSyncComparisons,
  setApproaches,
  combinedGPCComparisonPerfMetricAttrs,
}: SynchronizeCalendarYearsProps) => {
  if (isEmpty(financialsPeriods)) {
    return;
  }

  const calendarYears = [...new Set(financialsPeriods.map(p => p.statement_date?.slice(0, 4)))].join(',');

  const valuationSvc = new ValuationService();

  const tmpApproaches = valuationApproaches.map(async approach => {
    if (approach.approach_type === PUBLIC_COMPANIES) {
      const { gpc_comparison: comparisons } = approach.valuations_approach_gpc!;

      // eslint-disable-next-line no-param-reassign
      approach.valuations_approach_gpc!.gpc_comparison = await Promise.all(
        comparisons!.map(async comparison => {
          Object.assign(comparison, checkGpcComparison(comparison));

          if (!comparison.calendar_years_financials) {
            const { id: md } = measurementDate;
            const currency = companyInfo?.financials_currency as string || VALUATIONS_DEFAULT_CURRENCY;

            const defaultCalendarYearsFinancials = calendarYears.split(',').reduce<CalendarYearsFinancials>((sofar, year) => {
              // eslint-disable-next-line no-param-reassign
              sofar[Number(year)] = {
                revenue: 0,
                ebitda: 0,
              };
              return sofar;
            }, {});

            try {
              const gpc: PublicComp
                = comparison?.ticker_symbol === DATA_UNAVAILABLE
                  ? {} as PublicComp
                  : (await valuationSvc.getGpc(getTicker(comparison?.ticker_symbol as string), md!, currency, calendarYears)) as PublicComp;

              return {
                ...comparison,
                calendar_years_financials: isEmpty(gpc)
                  ? defaultCalendarYearsFinancials
                  : parseGpcComparison({
                    data: gpc,
                    attributes: combinedGPCComparisonPerfMetricAttrs,
                  }).calendar_years_financials,
              };
            } catch (e) {
              Logger.error('Error parsing GPC comparison', e);
              return {
                ...comparison,
                calendar_years_financials: defaultCalendarYearsFinancials,
              };
            }
          }

          return comparison;
        })
      );
    }

    return approach;
  });

  const updatedApproaches = await Promise.all(tmpApproaches);

  setSyncComparisons(true);
  setApproaches(updatedApproaches);
};

/**
 * Synchronize and update GPC Approaches
 * @param {Object} valuation
 * @param {Object} approaches
 * @param {array} financialsPeriods
 * @param {Object} measurementDate
 * @param {Object} companyInfo
 * @param {Function} setSyncComparisons
 * @param {Function} setApproaches
 * @returns {void}
 */
export const synchronizeAndUpdateApproaches = ({
  valuation,
  financialsPeriods,
  measurementDate,
  companyInfo,
  setSyncComparisons,
  setApproaches,
  combinedGPCComparisonPerfMetricAttrs,
}: SynchronizeAndUpdateApproachesProps) => {
  if (!isUndefined(valuation) && !isEmpty(valuation) && financialsPeriods) {
    const valuationApproaches = valuation
      .valuations_approaches!.map(approach => ({
        ...approach,
        panelId: approach?.panelId ?? approach?.panel_id ?? uuid(),
      }))
      .sort((approach1, approach2) => approach1.id! - approach2.id!);

    if (financialsPeriods.length) {
      synchronizeCalendarYears({
        valuationApproaches,
        financialsPeriods,
        measurementDate,
        companyInfo,
        setSyncComparisons,
        setApproaches,
        combinedGPCComparisonPerfMetricAttrs,
      });
    } else {
      setSyncComparisons(true);
      setApproaches(valuationApproaches);
    }
  }
};

export function getSelectionValue(row: any, column: any, approach: any) {
  if (!row.options.includes(column.selection) && column.selection) {
    // we get the percentile
    const percentileMap = {
      [SELECTION_A]: PERCENTILE_SELECTION_A_ALIAS,
      [SELECTION_B]: PERCENTILE_SELECTION_B_ALIAS,
    };
    const percentileKey = percentileMap[column.selection as keyof typeof percentileMap];
    return percentileOptions[approach[percentileKey] - 1].label;
  }
  return column.selection;
}

export function addBenchmarkCustomKeys(cell: ICell, tmpCell: ICell, column: any) {
  if (BENCHMARK_ALIASES.includes(cell.alias)) {
    // eslint-disable-next-line no-param-reassign
    tmpCell.customKey = `${column.id}_${cell.alias}`;
  }
}

export function handleMultipleCell(cell: ICell, column: any, useMultiplePremiumDiscount = false) {
  if (cell.alias === SELECTED_MULTIPLE_VALUE && column.selection) {
    const selectionMap = {
      Mean: MEAN_ALIAS,
      Median: MEDIAN_ALIAS,
      [SPECIFIED_LABEL]: SELECTED_MULTIPLE,
      [PERCENTILE_75]: SEVENTY_FIFTH_ALIAS,
      [PERCENTILE_25]: TWENTY_FIFTH_ALIAS,
      [SELECTION_A]: PERCENTILE_SELECTION_A_ALIAS,
      [SELECTION_B]: PERCENTILE_SELECTION_B_ALIAS,
    };
    const selection = selectionMap[column.selection as keyof typeof selectionMap];

    if (!useMultiplePremiumDiscount) {
      // eslint-disable-next-line no-param-reassign
      cell.expr = `=${column.id}_${selection}`;
    } else {
      // eslint-disable-next-line no-param-reassign
      cell.expr = `=${column.id}_${selection} * (1 + ${column.id}_MPD)`;
    }
  }
}
const TitleIconWrapper = ({ Icon, title }: TitleIconWrapperProps) => (
  <Grid component="span" container justifyContent="flex-end" alignItems="center">
    {title} <Icon fontSize="large" />
  </Grid>
);

export const getTitleWithIcon = ({ title, columnKey, sortedColumn }: GetTitleWithIconProps) => {
  if (sortedColumn && columnKey in sortedColumn) {
    return sortedColumn?.[columnKey] === SORT_ASC ? (
      <TitleIconWrapper Icon={ArrowDropUpIcon} title={title} />
    ) : (
      <TitleIconWrapper Icon={ArrowDropDownIcon} title={title} />
    );
  }

  return title;
};
